import { Tooltip } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React from 'react';

import { BidderGroupAPIKey, IBidderGroup } from '~/src/dash/api/bidder-group';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { Div, Text } from '~/src/dash/publicaUI';

export const formatBidderGroupsResponseIntoLVTRows = (
  bidderGroups: IBidderGroup[],
  cfg: IBidderGroupsListViewTable,
) => {
  const formattedRows = [] as IListViewTableRow[];
  bidderGroups.forEach((group) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = group[responseKey as keyof IBidderGroup];
      if (isNumeric) {
        value = Number(value);
      }
      if (formatValue) {
        value = formatValue(value);
      }

      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

const extractBidderGroupLabels = (value: any[]) => {
  const labels: string[] = [];
  value.forEach((item) => {
    if (value && !isEmpty(value)) {
      labels.push(item.Name);
    }
  });
  return labels;
};

const joinBidderGroupLabels = (value: any[]) => {
  if (!value || isEmpty(value)) {
    return '';
  }
  return extractBidderGroupLabels(value).join(', ');
};

const BidderGroupModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;

  return (
    <Tooltip title={value} placement='top-start'>
      <div
        onClick={() => customFunctions.handleClickOnName(props.row.ID.value)}
        style={{ cursor: 'pointer' }}
      >
        <Text
          small
          bold
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {value}
        </Text>
      </div>
    </Tooltip>
  );
};

const BiddersListModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div
      onClick={() => customFunctions.handleClickOnGroupedBidders(props.row.ID.value)}
      style={{ cursor: 'pointer' }}
    >
      <Text small bold>
        {value}
      </Text>
    </Div>
  );
};

interface IBidderGroupsListViewTable {
  columns: IListViewTableColumns;
}

export interface IBidderGroupsListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: BidderGroupAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  isInitialized?: boolean;
  columnSelector: {
    resetKeys?: BidderGroupAPIKey[]; // what keys should be made active on reset
  };
  table: IBidderGroupsListViewTable;
}

export const bidderGroupsListViewConfig = {
  cypressClassName: 'biddergroupslistview',
  defaultState: {
    activeColumnKeys: ['BidderCount', 'BidderGroupLabels'],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'BidderCount',
    page: 0,
    rowsPerPage: 25,
  },
  columnSelector: {
    resetKeys: ['BidderCount', 'BidderGroupLabels'],
  },
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        tKey: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: BidderGroupModalLink,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        tKey: 'Name',
        requestKey: '',
        responseKey: 'Name',
        valueType: 'string',
      },
      // we only need this value to store for reference
      BidderIDs: {
        isHidden: true,
        key: 'BidderIDs',
        label: 'Grouped Bidders',
        requestKey: '',
        responseKey: 'BidderIDs',
        valueType: 'array',
      },
      // this is calculated by using BidderIDs as a reference
      BidderCount: {
        cellJSX: BiddersListModalLink,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        isDefaultActive: true,
        key: 'BidderCount',
        label: 'Grouped Bidders',
        tKey: 'GroupedBidders',
        requestKey: '',
        responseKey: 'BidderCount',
        valueType: 'number',
      },
      BidderGroupLabels: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        formatValue: joinBidderGroupLabels,
        headerRenderType: 'string',
        isDefaultActive: true,
        isSearchable: true,
        key: 'BidderGroupLabels',
        label: 'Bidder Group Labels',
        tKey: 'BidderGroupLabels',
        requestKey: '',
        responseKey: 'BidderGroupLabels',
        valueType: 'array',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        tKey: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
    },
  },
} as IBidderGroupsListViewConfig;
